import React, {Component} from 'react';
import {connect} from 'react-redux';
import {restoreItems} from "../../store/actions/recoveryActions";
import {getRecoverySearchResults} from "../../store/actions/filterActions";
import {generateMonths} from "../../services/dateUtils";
import Loader from '../../components/UI-components/Loader';
import Button from '../../components/UI-components/Button';
import {Switch} from "../../components/UI-components/Switch";
import Checkbox from '../../components/UI-components/Checkbox';
import {getValueCheckbox} from "../../services/Api";
import Logo from "../../components/UI-components/Logo";
import RecoveryAreaTable from '../../components/RecoveryArea/RecoveryAreaTable';
import {DELETED_STATUS_OPTIONS} from "../../services/constList";
import Popup from "../../components/UI-components/Popup";

const VIEW_TYPES = { MODULES: 'module', CLUSTERS: 'cluster' };
const ITEMS_PER_PAGE = 50;
const MONTHS_TO_DISPLAY = generateMonths(15, 15)
const RESTORABLE_STATUS_VALUE = "DELETION_PENDING";

class RecoveryArea extends Component {
    constructor(props) {
        super(props);
        const allStatusValues = DELETED_STATUS_OPTIONS.map(opt => opt.value);
        this.state = {
            activeView: VIEW_TYPES.MODULES,
            selectedStatuses: allStatusValues,
            selectedItemIds: [],
            groupedItems: {},
            currentPage: 1,
            itemsPerPage: ITEMS_PER_PAGE,
            pageInput: '1',
            showFilterPrompt: false,
            isRestoreConfirmModalOpen: false,
        };
    }

    componentDidMount() {
        if (this.state.selectedStatuses.length > 0) {
            this.fetchData();
        } else {
            this.setState({ showFilterPrompt: true });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevState.activeView !== this.state.activeView ||
            JSON.stringify(prevState.selectedStatuses) !== JSON.stringify(this.state.selectedStatuses)
        ) {
            this.setState({
                selectedItemIds: [],
                groupedItems: {},
                currentPage: 1,
                pageInput: '1',
                showFilterPrompt: this.state.selectedStatuses.length === 0,
            }, () => {
                if (this.state.selectedStatuses.length > 0) {
                    this.fetchData();
                }
            });
        }

        if (prevProps.items !== this.props.items && !this.state.showFilterPrompt) {
            this.transformData(this.props.items);
            this.setState({ pagination: this.props.pagination });
        }

        if (prevState.itemsPerPage !== this.state.itemsPerPage && !this.state.showFilterPrompt) {
            const totalPages = this.state?.pagination?.totalPages;
            if (this.state.currentPage > totalPages) {
                this.setState({
                    pagination: this.props.pagination,
                    currentPage: Math.max(1, totalPages),
                    pageInput: Math.max(1, totalPages).toString(),
                });
            }
        }
    }



    fetchData = (pageNumberToFetch = undefined) => {
        const { activeView, selectedStatuses } = this.state;
        const { currentFilters, exportPagination } = this.props;

        if (selectedStatuses.length === 0) {
            console.log("No status selected, clearing data and skipping fetch.");
            this.setState({
                groupedItems: {},
                selectedItems: [],
                currentPage: 1,
                pageInput: '1',
                showFilterPrompt: true
            });
            return;
        }

        this.setState({ showFilterPrompt: false });

        const pageNumber = pageNumberToFetch !== undefined ? (pageNumberToFetch - 1) : (exportPagination?.currentPage ?? 0);
        const pageSize = exportPagination?.pageSize || ITEMS_PER_PAGE;

        //console.log(`Fetching data for ${activeView}, statuses: [${selectedStatuses.join(', ')}], page ${pageNumber}, size ${pageSize}`);

        this.props.getRecoverySearchResults(
            activeView,
            currentFilters,
            selectedStatuses,
            null,
            pageNumber,
            pageSize,
            true
        );
    };


    transformData = (rawItems) => {
        const { activeView } = this.state;
        const transformedGroupedItems = {};

        if (!rawItems || !Array.isArray(rawItems)) {
            console.warn("transformData received invalid rawItems:", rawItems);
            this.setState({ groupedItems: {} });
            return;
        }

        rawItems.forEach(item => {
            const isModule = activeView === VIEW_TYPES.MODULES;
            const id = isModule ? item.moduleName : item.clusterId;
            const label = isModule ? item.moduleLabel : item.clusterLabel;

            if (!id) {
                console.warn("Item missing ID:", item);
                return;
            }

            const overallStatus = item.openMonthList.length > 0 ? item.openMonthList[0].status : null;
            const overallStatusMonthId = item.deleteFromMonthId;

            const isError = overallStatus === "DELETION_ERROR";
            const errorMessage = isError ? (item.errorMessage || "An error occurred during deletion.") : "";

            const monthsObject = {};
            const itemMonthsMap = new Map(
                (Array.isArray(item.openMonthList) ? item.openMonthList : []).map(m => [m.monthId, m])
            );

            for (const monthId of MONTHS_TO_DISPLAY) {
                const monthData = itemMonthsMap.get(monthId);
                if (monthData) {
                    monthsObject[monthId] = {
                        status: monthData.status,
                        originalItem: monthData,
                        createdAfter: monthData.createdAfterDelete,
                        uiVisible: monthData.uiVisible
                    };
                } else {
                    monthsObject[monthId] = null;
                }
            }

            transformedGroupedItems[id] = {
                id: id,
                label: label || id,
                overallStatus: overallStatus,
                errorMessage: errorMessage,
                overallStatusMonthId: overallStatusMonthId,
                months: monthsObject,
                ...(isModule ? {} : { storeJda: item.storeJda, codiceStoreSap: item.codiceStoreSap }),
            };
        });

        this.setState({ groupedItems: transformedGroupedItems });
    };

    handleViewToggle = () => {
        this.setState(prevState => ({
            activeView: prevState.activeView === VIEW_TYPES.MODULES
                ? VIEW_TYPES.CLUSTERS
                : VIEW_TYPES.MODULES
        }));
    };

    handleStatusChange = (value, isChecked) => {
        const updatedStatuses = getValueCheckbox(
            value,
            isChecked,
            this.state.selectedStatuses
        );
        this.setState({ selectedStatuses: updatedStatuses });
    };

    handleSelectAll = (currentItemIdsOnPage, areAllOnPageSelected) => {
        this.setState(prevState => {
            const currentSelection = new Set(prevState.selectedItemIds);
            if (areAllOnPageSelected) {
                currentItemIdsOnPage.forEach(id => currentSelection.delete(id));
            } else {
                currentItemIdsOnPage.forEach(id => currentSelection.add(id));
            }
            return { selectedItemIds: Array.from(currentSelection) };
        });
    };

    handleRowSelect = (itemId) => {
        this.setState(prevState => {
            const prevSelected = prevState.selectedItemIds;
            if (prevSelected.includes(itemId)) {
                return { selectedItemIds: prevSelected.filter(id => id !== itemId) };
            } else {
                return { selectedItemIds: [...prevSelected, itemId] };
            }
        });
    };

    openRestoreConfirmModal = () => {
        this.setState({ isRestoreConfirmModalOpen: true });
    };

    closeRestoreConfirmModal = () => {
        this.setState({ isRestoreConfirmModalOpen: false });
    };

    handleRestoreSelected = () => {
        const { selectedItemIds } = this.state;

        if (selectedItemIds.length === 0) {
            alert(`No items selected for restoration.`);
            return;
        }
        this.openRestoreConfirmModal();
    };

    handleConfirmRestore = () => {
        const { selectedItemIds, activeView } = this.state;
        const { currentFilters } = this.props;

        if (selectedItemIds.length === 0) {
            this.closeRestoreConfirmModal();
            return;
        }

        this.props.restoreItems(selectedItemIds, activeView, currentFilters);

        this.setState({ selectedItemIds: [] });
        this.closeRestoreConfirmModal();
    };

    handlePageChange = (pageNumber) => {
        const totalPages = this.state?.pagination?.totalPages || 1;
        const validPageNumber = Math.max(1, Math.min(pageNumber, totalPages));

        this.setState({
            currentPage: validPageNumber,
            pageInput: validPageNumber.toString()
        });
        this.fetchData(validPageNumber);
    };

    handlePageInputChange = (e) => {
        this.setState({ pageInput: e.target.value });
    }

    handlePageInputBlur = () => {
        const totalPages = this.state?.pagination?.totalPages || 1;
        let targetPage = parseInt(this.state.pageInput, 10);
        const currentPage = this.state.currentPage;

        if (isNaN(targetPage) || targetPage < 1) {
            targetPage = 1;
        } else if (targetPage > totalPages) {
            targetPage = totalPages;
        }

        if (targetPage !== currentPage) {
            this.handlePageChange(targetPage);
        } else {
            this.setState({ pageInput: currentPage.toString() });
        }
    };


    handlePageInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            this.handlePageInputBlur();
            e.target.blur();
        }
    }

    renderPaginationControls = () => {
        const { currentPage, pageInput } = this.state;
        const { isLoading, isRestoring } = this.props;
        const totalPages = this.state?.pagination?.totalPages || 1;

        return (
            <div className="pagination-controls">
                <span className="page-info page-input-container">
                    Go to page
                    <input
                        type="number"
                        value={pageInput}
                        onChange={this.handlePageInputChange}
                        onBlur={this.handlePageInputBlur}
                        onKeyDown={this.handlePageInputKeyDown}
                        min="1"
                        max={totalPages}
                        className="page-input"
                        aria-label={`Current page, edit to jump to page. Total pages: ${totalPages}`}
                        disabled={isLoading || isRestoring}
                    />
                    <Button
                        btnClass="btn-small btn-pagination"
                        handleClick={this.handlePageInputBlur}
                        disabled={isLoading || isRestoring}
                    >
                        Apply
                    </Button>
                 </span>
                <div className={"pagination-buttons-group"}>
                    <Button
                        btnClass="btn-small btn-pagination"
                        handleClick={() => {this.handlePageChange(currentPage - 1)}}
                        disabled={currentPage === 1 || isLoading || isRestoring} // Disable button
                    >
                        &laquo; Previous
                    </Button>
                    <span className="page-info">
                    Page {currentPage} of {totalPages}
                 </span>
                    <Button
                        btnClass="btn-small btn-pagination"
                        handleClick={() => {this.handlePageChange(currentPage + 1)}}
                        disabled={currentPage === totalPages || isLoading || isRestoring} // Disable button
                    >
                        Next &raquo;
                    </Button>
                </div>
            </div>
        );
    }


    render() {
        const {
            activeView,
            selectedStatuses,
            selectedItemIds,
            groupedItems,
            currentPage,
            itemsPerPage,
            isRestoreConfirmModalOpen,
        } = this.state;

        const { isLoading, fetchError, isRestoring } = this.props;

        console.log("currentPage: " + currentPage);
        console.log("itemsPerPage: " + itemsPerPage);

        const allItemIds = Object.keys(groupedItems);
        const totalItems = allItemIds.length;
        const currentItemIdsOnPage = allItemIds;

        const isBusy = isLoading || isRestoring;

        const renderEmptyState = (message) => (
            <div className="empty-state-container">
                <Logo />
                <p className="empty-state-message">{message}</p>
            </div>
        );

        const itemTypeLabel = activeView === VIEW_TYPES.MODULES ? 'module' : 'cluster';

        const selectedItemsForModal = selectedItemIds.map(id => ({
            id: id,
            label: groupedItems[id] ? groupedItems[id].label : 'Unknown Item'
        }));

        const restorableItemsIdsOnPage = currentItemIdsOnPage.filter(
            itemId => groupedItems[itemId] && groupedItems[itemId].overallStatus === RESTORABLE_STATUS_VALUE
        );

        const selectedRestorableCount = restorableItemsIdsOnPage.filter(id => selectedItemIds.includes(id)).length;

        const areAllRestorableOnPageSelected = restorableItemsIdsOnPage.length > 0 &&
            selectedRestorableCount === restorableItemsIdsOnPage.length;

        const isIndeterminate = selectedRestorableCount > 0 && selectedRestorableCount < restorableItemsIdsOnPage.length;


        return (
            <div className="recovery-area-page">
                <header className="minimal-header">
                    <Logo />
                    <h2>Deletion Area</h2>
                    <div className="recovery-controls-switch">
                        <div className="option-value">
                            <Switch
                                isChecked={activeView === VIEW_TYPES.CLUSTERS}
                                onChange={this.handleViewToggle}
                                label={["Modules", "Clusters"]}
                                disabled={isBusy}
                            />
                        </div>
                    </div>
                </header>
                <div className="recovery-area-container">

                    <div className="recovery-controls">
                        <div className="option-type">Status: </div>
                        <div className="option-value checkbox-container">
                            {DELETED_STATUS_OPTIONS.map(opt => (
                                <Checkbox
                                    key={opt.value}
                                    value={opt.value}
                                    checked={selectedStatuses.includes(opt.value)}
                                    handleClick={this.handleStatusChange}
                                    disabled={isBusy}
                                >
                                    {opt.label}
                                </Checkbox>
                            ))}
                        </div>
                    </div>

                    <div className="recovery-results">
                        {this.state.showFilterPrompt ? (
                            renderEmptyState("Please select at least one status filter to view items.")
                        ) : isBusy ? (
                            <Loader />
                        ) : fetchError ? (
                            renderEmptyState(`Error: ${fetchError || 'Failed to load data. Please try again.'}`)
                        ) : totalItems === 0 ? (
                            renderEmptyState("No items found matching the selected status filters.")
                        ) : (
                            <>
                                <div className="action-buttons">
                                    <Button
                                        btnClass="btn-medium btn-restore"
                                        handleClick={this.handleRestoreSelected}
                                        disabled={isBusy || selectedItemIds.length === 0}
                                    >
                                        {isRestoring ? "Restoring..." : `Restore Selected (${selectedItemIds.length})`}
                                    </Button>
                                </div>
                                <RecoveryAreaTable
                                    itemIdsOnPage={currentItemIdsOnPage}
                                    groupedItems={groupedItems}
                                    selectedItemIds={selectedItemIds}
                                    isLoading={isBusy}
                                    activeView={activeView}
                                    onSelectAll={this.handleSelectAll}
                                    onRowSelect={this.handleRowSelect}
                                    areAllOnPageSelected={areAllRestorableOnPageSelected}
                                    isIndeterminate={isIndeterminate}
                                    restorableItemsIdsOnPage={restorableItemsIdsOnPage}
                                />
                                {this.renderPaginationControls()}
                            </>
                        )}
                    </div>
                </div>
                {isRestoreConfirmModalOpen && (
                    <Popup
                        popupClass="popup-medium popup-restore-items"
                        title="Confirm Restoration"
                        isDoubleButton
                        btnName="Confirm"
                        btnName2="Cancel"
                        close={this.closeRestoreConfirmModal}
                        handleClick={this.handleConfirmRestore}
                    >
                        <div className="restore-items-list-container">
                            <p style={{ marginBottom: '15px', textAlign: 'center' }}>
                                Are you sure you want to restore the following{' '}{itemTypeLabel}(s)?
                            </p>
                            <div className="restore-items-list scroll">
                                {selectedItemsForModal.map(item => (
                                    <div key={item.id} className="restore-item-row">
                                        <span className="restore-item-label">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Popup>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        items: state.filterRecovery.exportResults || [],
        pagination: state.filterRecovery.exportPagination,
        isLoading: state.filterRecovery.isExportSearchLoading,
        fetchError: state.filterRecovery.error,
        isRestoring: state.filterRecovery.isRestoring,
        restoreError: state.filterRecovery.restoreError,
        currentFilters: state.filterRecovery.exportSelectedFilters,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getRecoverySearchResults: (type, filters, status, moduleType, pageNumber, pageSize, dry) =>
            dispatch(getRecoverySearchResults(type, filters, status, moduleType, pageNumber, pageSize, dry)),
        restoreItems: (itemsToRestore, viewType, currentFilters) =>
            dispatch(restoreItems(itemsToRestore, viewType, currentFilters)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RecoveryArea);